import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiZap } from 'react-icons/fi';
import * as Icons from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Card, DataTable, Button, Badge, ConfirmDialog, Toggle, Modal, Select, RichTextEditor } from '../../components/common';
import { serviceService } from '../../services/serviceService';
import ReviewFormInput from '../reviews/ReviewFormInput'
import { truncate } from '../../utils/stringUtils';
import { useDebounce, useModal, useForm } from '../../hooks';
import { validators } from '../../utils/validators';
import './ServicesPage.css';

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [iconOptions, setIconOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [totalItems, setTotalItems] = useState(0);
  
  const formModal = useModal();
  const deleteModal = useModal();
  const [saving, setSaving] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  const validationRules = {
    title: [validators.required, validators.minLength(3)],
    shortDescription: [validators.required],
  };

  const form = useForm({
    title: '',
    icon: 'FaBuilding',
    shortDescription: '',
    detailedDescription: '',
    isActive: true
  }, validationRules);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await serviceService.getAll({ search: debouncedSearch });
      setServices(response.data.services);
      setTotalItems(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  const fetchIconOptions = async () => {
    try {
      const response = await serviceService.getIconOptions();
      setIconOptions(response.data.map(icon => ({ value: icon, label: icon })));
    } catch (error) {
      console.error('Failed to fetch icon options');
    }
  };

  useEffect(() => {
    fetchServices();
    fetchIconOptions();
  }, [fetchServices]);

  const handleSearch = (value) => {
    setSearch(value);
  };

  const handleOpenForm = async (service = null) => {
    form.reset();
    formModal.open(service);
    if (service) {
      setFormLoading(true);
      try {
        const response = await serviceService.getById(service.id);
        const s = response.data;
        form.setValues({
          id: s.id,
          title: s.title,
          icon: s.icon || 'FaBuilding',
          shortDescription: s.shortDescription || '',
          detailedDescription: s.detailedDescription || '',
          isActive: s.isActive,
        });
      } catch (error) {
        toast.error('Failed to load service details');
        formModal.close();
      } finally {
        setFormLoading(false);
      }
    }
  };

  const demoData = [
    {
      title: 'Structural Design',
      icon: 'FaDraftingCompass',
      shortDescription: 'High quality structural planning for residential and commercial buildings.',
      detailedDescription: '<p>Our <strong>Structural Design</strong> service delivers end-to-end structural solutions. We combine cutting-edge simulation software with decades of field expertise to ensure every build is safe, cost-efficient, and built to last.</p><ul><li>Load analysis &amp; calculations</li><li>Foundation design</li><li>Steel &amp; RCC detailing</li></ul>',
      isActive: true,
    },
    {
      title: 'Interior Architecture',
      icon: 'FaCouch',
      shortDescription: 'Creative interiors that balance aesthetics with everyday functionality.',
      detailedDescription: '<p>From concept boards to final handover, our <strong>Interior Architecture</strong> team transforms spaces into experiences. We handle space planning, material selection, lighting design, and custom furniture.</p>',
      isActive: true,
    },
    {
      title: 'Project Management',
      icon: 'FaTasks',
      shortDescription: 'End-to-end project management ensuring timely delivery within budget.',
      detailedDescription: '<p>We oversee every phase of your project — from procurement and contractor coordination to quality audits and final commissioning — so you can focus on your core business.</p>',
      isActive: true,
    },
  ];

  const handleFillDemo = () => {
    const sample = demoData[Math.floor(Math.random() * demoData.length)];
    form.setValues({
      ...form.values,
      ...sample,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.validate()) {
      toast.error('Please fix the form errors');
      return;
    }
    
    setSaving(true);
    try {
      if (formModal.data) {
        await serviceService.update(formModal.data.id, form.values);
        toast.success('Service updated successfully');
      } else {
        await serviceService.create(form.values);
        toast.success('Service added successfully');
      }
      
      formModal.close();
      fetchServices();
    } catch (error) {
      toast.error('Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await serviceService.toggleStatus(id);
      const updated = response?.data;
      setServices(prev => prev.map(item =>
        item.id === id
          ? { ...item, isActive: updated?.isActive !== undefined ? updated.isActive : !item.isActive }
          : item
      ));
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await serviceService.delete(deleteModal.data);
      toast.success('Service deleted successfully');
      fetchServices();
    } catch (error) {
      toast.error('Failed to delete service');
    } finally {
      setDeleteLoading(false);
      deleteModal.close();
    }
  };

  const getIconComponent = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent /> : <Icons.FaBuilding />;
  };

  const columns = [
    {
      header: 'Service',
      render: (row) => (
        <div className="service-cell">
          <div className="service-icon">
            {getIconComponent(row.icon)}
          </div>
          <div className="service-info">
            <span className="service-title">{row.title}</span>
            <span className="service-desc">{truncate(row.shortDescription, 60)}</span>
          </div>
        </div>
      )
    },
    {
      header: 'Status',
      width: '100px',
      render: (row) => (
        <Toggle
          name={`status-${row.id}`}
          checked={row.isActive}
          onChange={() => handleToggleStatus(row.id)}
          size="small"
        />
      )
    },
    {
      header: 'State',
      width: '100px',
      render: (row) => (
        <Badge variant={row.isActive ? 'success' : 'warning'}>
          {row.isActive ? 'Active' : 'Inactive'}
        </Badge>
      )
    },
    {
      header: 'Actions',
      width: '100px',
      align: 'center',
      render: (row) => (
        <div className="action-buttons">
          <button 
            className="action-btn edit"
            title="Edit"
            onClick={() => handleOpenForm(row)}
          >
            <FiEdit2 />
          </button>
          <button 
            className="action-btn delete"
            title="Delete"
            onClick={() => deleteModal.open(row.id)}
          >
            <FiTrash2 />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="services-page">
      <Card
        title="Services"
        subtitle={`${totalItems} services`}
        action={
          <Button icon={<FiPlus />} onClick={() => handleOpenForm()}>
            Add Service
          </Button>
        }
      >
        <DataTable
          columns={columns}
          data={services}
          loading={loading}
          searchable
          searchPlaceholder="Search services..."
          searchValue={search}
          onSearch={handleSearch}
          exportable
          exportFilename="services-export"
          emptyMessage="No services found"
        />
      </Card>

      {/* Form Modal */}
      <Modal
        isOpen={formModal.isOpen}
        onClose={formModal.close}
        title={formModal.data ? 'Edit Service' : 'Add Service'}
        size="large"
        loading={formLoading}
        footer={
          <>
            {!formModal.data && (
              <Button
                variant="ghost"
                icon={<FiZap />}
                onClick={handleFillDemo}
                title="Fill form with sample service data"
                style={{ marginRight: 'auto' }}
              >
                Demo Data
              </Button>
            )}
            <Button variant="ghost" onClick={formModal.close}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={saving}>
              {formModal.data ? 'Update' : 'Save'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="service-form">
          <div className="sf-row">
            <div className={`sf-field ${form.errors.title && form.touched.title ? 'sf-field--error' : ''}`}>
              <label className="sf-label" htmlFor="sf-title">
                Service Title <span className="sf-required">*</span>
              </label>
              <input
                id="sf-title"
                name="title"
                type="text"
                className="sf-input"
                placeholder="Enter service title"
                value={form.values.title}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
              />
              {form.errors.title && form.touched.title && (
                <span className="sf-error">{form.errors.title}</span>
              )}
            </div>
            <div className="sf-icon-group">
              <label className="sf-label">Icon</label>
              <div className="sf-icon-row">
                <Select
                  name="icon"
                  value={form.values.icon}
                  onChange={form.handleChange}
                  options={iconOptions}
                  placeholder="Select icon"
                />
                <div className="sf-icon-badge">
                  {getIconComponent(form.values.icon)}
                </div>
              </div>
            </div>
          </div>

          <div className={`sf-field ${form.errors.shortDescription && form.touched.shortDescription ? 'sf-field--error' : ''}`}>
            <label className="sf-label" htmlFor="sf-shortDesc">
              Short Description <span className="sf-required">*</span>
            </label>
            <textarea
              id="sf-shortDesc"
              name="shortDescription"
              className="sf-textarea"
              placeholder="Enter a brief description"
              rows={3}
              value={form.values.shortDescription}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
            />
            {form.errors.shortDescription && form.touched.shortDescription && (
              <span className="sf-error">{form.errors.shortDescription}</span>
            )}
          </div>
          <RichTextEditor
            label="Detailed Description"
            value={form.values.detailedDescription}
            onChange={(content) => form.setFieldValue('detailedDescription', content)}
            placeholder="Enter detailed description..."
            minHeight={150}
          />
          <Toggle
            name="isActive"
            checked={form.values.isActive}
            onChange={(e) => form.setFieldValue('isActive', e.target.checked)}
            label="Active"
          />
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        title="Delete Service"
        message="Are you sure you want to delete this service? This action cannot be undone."
        confirmText="Delete"
        loading={deleteLoading}
      />
    </div>
  );
};

export default ServicesPage;
